// src/pages/CourseTime/components/activities/PollActivity.comp.jsx
import React, { useState } from 'react';
import * as S from '../../styles';
import PollResultsModal from '../PollResultsModal.comp';
import { GET_APP_LOCALIZE_TEXT } from '../../../../utils/dq_lc_service/lc_service';
import { renderPlainTextWithFormatting, markdownInlineToHtml } from '../../utils';


const PollActivity = ({
  slide,
  renderSpokenText,
  selectedPollOption,
  selectedPollOptions,
  setSelectedPollOption,
  setSelectedPollOptions,
  onSubmit,
  onEditActivity,
  interactionLoading,
  fetchingExistingRecord,
  inputsDisabled,
  activityCompleted,
  // Additional props for poll results
  module_code,
  session,
  segment,
  slideIndex,
  themeColor,
  pageLc
}) => {
  const [showResultsModal, setShowResultsModal] = useState(false);
  
  const isMAQ = slide.Category11 === 'MAQ';
  const hasValidSelection = isMAQ 
    ? selectedPollOptions.length > 0 
    : selectedPollOption !== null;

  const handleOptionClick = (optionIndex) => {
    if (inputsDisabled) return; // Don't allow clicks when disabled
    
    if (isMAQ) {
      // Multiple Answer Question - toggle option in array
      setSelectedPollOptions(prev => {
        if (prev.includes(optionIndex)) {
          return prev.filter(idx => idx !== optionIndex);
        } else {
          return [...prev, optionIndex];
        }
      });
    } else {
      // Multiple Choice Question - single selection
      setSelectedPollOption(optionIndex);
    }
  };

  const isOptionSelected = (optionIndex) => {
    return isMAQ 
      ? selectedPollOptions.includes(optionIndex)
      : selectedPollOption === optionIndex;
  };

  const renderHtmlOrTTS = (key, value) => {
    if (value === null || value === undefined) return null;
    const text = String(value).trim();
    const hasHtml = /<[^>]+>/.test(text);

    if (hasHtml) {
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    }

    // Plain text may carry markdown (**bold**, _italic_). Real HTML content
    // above already forfeits TTS word-highlighting in favor of
    // dangerouslySetInnerHTML, so do the same once markdown is converted to
    // tags — otherwise renderSpokenText below prints the raw markers literally.
    const withInlineTags = markdownInlineToHtml(text);
    if (/<[^>]+>/.test(withInlineTags)) {
      return <span dangerouslySetInnerHTML={{ __html: withInlineTags }} />;
    }

    return renderSpokenText ? renderSpokenText(key, text) : renderPlainTextWithFormatting(text);
  };

  return (
    <S.ActivityWrapper>
      <S.ActivityColumnContainer>
        <S.ActivityHeader>
          <S.ActivityPill>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__poll_time')}!</S.ActivityPill>
          <S.ActivityPollTitle>
            <span>{renderHtmlOrTTS('poll_title', slide.SlideTitle)}</span>
          </S.ActivityPollTitle>
          <S.ActivityContent>
            <span>{renderHtmlOrTTS('poll_content', slide.SlideContent)}</span>
          </S.ActivityContent>
        </S.ActivityHeader>

        <S.PollContainer>
          {Array.isArray(slide.Options) && slide.Options.map((option, idx) => {
            const letter = String.fromCharCode(65 + idx);
            const selected = isOptionSelected(idx);
            return (
              <S.PollOption
                key={idx}
                selected={selected}
                disabled={inputsDisabled}
                onClick={() => handleOptionClick(idx)}
                themeColor={themeColor}
              >
                <S.PollOptionBadge selected={selected} themeColor={themeColor}>
                  {letter}
                </S.PollOptionBadge>
                <S.PollOptionText selected={selected}>
                  {isMAQ && (
                    <span style={{marginRight: '8px'}}>
                      {selected ? '☑' : '☐'}
                    </span>
                  )}
                  <span>{renderHtmlOrTTS(`poll_option_${idx}`, option)}</span>
                </S.PollOptionText>
              </S.PollOption>
            );
          })}
        </S.PollContainer>

        {/* Before submit: show primary Submit button */}
        {!activityCompleted[slide.SlideID] && (
          <S.SubmitButton
            onClick={() => onSubmit(slide)}
            disabled={!hasValidSelection || interactionLoading || inputsDisabled}
          >
            {interactionLoading ? GET_APP_LOCALIZE_TEXT(pageLc, 'ct__submitting') + '...' : GET_APP_LOCALIZE_TEXT(pageLc, 'ct__submit')}
          </S.SubmitButton>
        )}

        {/* After submit: hide Submit and show inline "View results" + "Edit response" */}
        {activityCompleted[slide.SlideID] && (
          <S.ResponseSavedWrapper>
            <S.PollActionButton
              $variant="pink"
              onClick={() => setShowResultsModal(true)}
              disabled={interactionLoading}
            >
              <S.PollIcon className="fa fa-pie-chart" />
              {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__view_poll_results')}
            </S.PollActionButton>
            {!fetchingExistingRecord && inputsDisabled && (
              <S.PollActionButton
                $variant="teal"
                onClick={() => onEditActivity(slide)}
                disabled={interactionLoading}
                title={GET_APP_LOCALIZE_TEXT(pageLc, 'ct__edit_response')}
              >
                <i className="fa fa-pencil" style={{ transform: 'scaleX(-1)' }} />
                {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__edit_response')}
              </S.PollActionButton>
            )}
          </S.ResponseSavedWrapper>
        )}
      </S.ActivityColumnContainer>

      {/* Loading overlay */}
      {fetchingExistingRecord && (
        <S.ActivityLoadingOverlay>
          <S.LoadingSpinner />
          <S.LoadingText>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__loading_existing_record')}...</S.LoadingText>
        </S.ActivityLoadingOverlay>
      )}

      {/* Poll Results Modal */}
      <PollResultsModal
        isOpen={showResultsModal}
        onClose={() => setShowResultsModal(false)}
        module_code={module_code}
        session={session}
        segment={segment}
        slide={slideIndex}
        pollType={isMAQ ? 'MAQ' : 'MCQ'}
        question={slide.SlideTitle}
        options={slide.Options || []}
        themeColor={themeColor}
        pageLc={pageLc}
      />
    </S.ActivityWrapper>
  );
};

export default PollActivity;
